import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { format as formatDate } from 'date-fns';
import Decimal from 'decimal.js';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Button, ConfirmationModal, CustomModal, FullPageModal, FullPageModalHeaderActions, Typography, normalizeDescription, useDevice, useOpenUnsavedChangesModal, useOverlayContext } from 'seagull';
import { Checkmark, CloseCross } from 'seagull/icons';
import { ServiceDescriptionHelpMeWriteDrawer } from '~/components/ai/HelpMeWriteDrawer/wirings/ServiceDescriptionHelpMeWriteDrawer';
import IncludedServices from '~/components/IncludedServices/IncludedServices';
import { MobileDeviceEnhancer } from '~/components/MobileDeviceEnhancer/MobileDeviceEnhancer';
import { MobileGroupingCard } from '~/components/MobileGroupingCard/MobileGroupingCard';
import { useStore, useStoreActions } from '~/contexts';
import { useAccountingItems } from '~/hooks/reactQuery/queries/useAccountingItems';
import { useIntegrationsSummary } from '~/hooks/reactQuery/queries/useIntegrationsSummary';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { handlePromise } from '~/infra/utils';
import { getPMDetails } from '~/integrations/PracticeManagement/pm.utils';
import PMMapServiceField from '~/integrations/PracticeManagement/PMMapServiceField/PMMapServiceField';
import { DiscountType } from '~/models';
import { isFromAmendment } from '~/models/service/selectors';
import { resolveStageScheduleContext } from '~/models/service/stageScheduleContext';
import { PreapprovalPeriodType } from '~/models/service-billing/model';
import { ServiceTemplateUsedIn } from '~/models/service-template/model';
import { fromDateStampObject } from '~/models/time';
import { selectors } from '~/store/state-selectors';
import { useAppConfig } from '~/store/store-context';
import { Accounting, PracticeManagement } from '~/types/api.model';
import { filterServiceOnlyTemplates } from '~/utils/filterServiceOnlyTemplates';
import { getHelperText, isValueTypeDollars, stagesTextsPath } from './BillingStagesSelect/BillingStagesSelect.utils';
import BillingTriggerDropdown from './BillingTrigger/BillingTriggerDropdown';
import DiscountInput from './DiscountInput';
import MinPriceTextField from './MinPriceTextField';
import OccurrenceTextField from './OccurrenceTextField/OccurrenceTextField';
import PreapprovedSection from './PreapprovedSection/PreapprovedSection';
import PriceTextField from './PriceTextField';
import PricingTextField from './PricingTextField/PricingTextField';
import ServiceAccountingField from './ServiceAccountingField';
import styles from './ServiceBuilder.module.scss';
import { getDefaultServiceMapping, getPriceAfterDiscount, isNameFieldDisabled, outputToService, quillToText, serviceBuilderTextsPath, serviceToInput } from './ServiceBuilder.utils';
import ServiceDescription from './ServiceDescription/ServiceDescription';
import ServiceNameTextField from './ServiceNameTextField/ServiceNameTextField';
import ServiceTerms from './ServiceTerms/ServiceTerms';
import UnitNameTextField from './UnitNameTextField';
import UpdateExistingServicesContent from './UpdateExistingServicesContent/UpdateExistingServicesContent';
const formId = 'service-builder-form';
const rootTestId = 'service-builder';
export const defaultInitialState = {
    price: '',
    pricing: 'fixed',
    trigger: 'approval',
    unitCap: '',
    minPrice: '',
    unitName: '',
    discount: {
        type: DiscountType.Percent,
        amount: ''
    },
    serviceName: '',
    description: '',
    terms: '',
    karbonMapping: [],
    keeperMapping: [],
    clientHubMapping: [],
    recurrencePeriod: 'onetime',
    serviceQboMapping: {
        type: 'unmapped'
    },
    serviceXeroMapping: {
        type: 'unmapped'
    },
    financialCentsMapping: [],
    preapprovalPeriod: PreapprovalPeriodType.None,
    oneTimeInStagesBilling: null
};
function ServiceBuilderContent(props) {
    var _ref;
    const { service, agreementData, serviceOrigin, serviceUsedIn, serviceNameBlacklist, applyOutputToChanges, isCreatingBundle, onSave, dismissModal, registerCanDismiss } = props;
    const { fetchKarbonItems, fetchClientHubItems, fetchFinancialCentsItems, fetchKeeperItems, logBI } = useStoreActions();
    const openUnsavedChangesModal = useOpenUnsavedChangesModal();
    const disableIntegrationsMapping = !!service && serviceOrigin !== 'template';
    const fullPageModal = !service || serviceOrigin === 'template';
    const showQboIntegration = useStore(selectors.integration.showQboIntegration);
    const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
    const { items: qboItems, refresh: refreshQboItems } = useAccountingItems(Accounting.QBO);
    const { items: xeroItems, refresh: refreshXeroItems } = useAccountingItems(Accounting.Xero);
    const showKarbonIntegration = useStore(selectors.integration.showKarbonIntegration);
    const showClientHubIntegration = useStore(selectors.integration.showClientHubIntegration);
    const showKeeperIntegration = useStore(selectors.integration.showKeeperIntegration);
    const showFinancialCentsIntegration = useStore(selectors.integration.showFinancialCentsIntegration);
    useIntegrationsSummary({
        enabled: showQboIntegration || showFinancialCentsIntegration || showKarbonIntegration || showClientHubIntegration,
        refreshOnHookMount: true
    });
    const financialCentsItems = useStore(selectors.integration.financialCentsItems);
    const karbonItems = useStore(selectors.integration.karbonItems);
    const clientHubItems = useStore(selectors.integration.clientHubItems);
    const keeperItems = useStore(selectors.integration.keeperItems);
    const { SERVICE_LEVEL_TERMS, AUTOMATIC_STAGES } = useFeatureFlags();
    const { isMobileDevice } = useDevice();
    const isFullScreen = fullPageModal || isMobileDevice;
    const { t: sbTexts } = useLocaleKeys(serviceBuilderTextsPath);
    const { t: stagesTexts } = useLocaleKeys(stagesTextsPath);
    const { generateUniqueId } = useAppConfig();
    const [saving, setSaving] = useState(false);
    const [isHelpMeWriteDrawerOpen, setIsHelpMeWriteDrawerOpen] = useState(false);
    const [drawerWidth, setDrawerWidth] = useState(564);
    const overlayContext = useOverlayContext();
    const rightSectionRef = useRef(null);
    const defaultValues = useMemo(()=>service ? serviceToInput({
            service,
            qboItems: (qboItems === null || qboItems === void 0 ? void 0 : qboItems.items) || [],
            xeroItems: (xeroItems === null || xeroItems === void 0 ? void 0 : xeroItems.items) || []
        }) : _object_spread_props(_object_spread({}, defaultInitialState), {
            bundle: isCreatingBundle ? [] : undefined
        }), [
        service,
        qboItems === null || qboItems === void 0 ? void 0 : qboItems.items,
        xeroItems === null || xeroItems === void 0 ? void 0 : xeroItems.items,
        isCreatingBundle
    ]);
    const isBundle = Boolean(defaultValues.bundle);
    const showPracticeManagementIntegration = (shouldShow)=>shouldShow && !isBundle;
    const headerText = (()=>{
        if (service) return isBundle ? sbTexts.header.editBundle() : sbTexts.header.editService();
        return isBundle ? sbTexts.header.createBundle() : sbTexts.header.createService();
    })();
    const onClose = ()=>dismissModal();
    const form = useForm({
        mode: 'all',
        defaultValues
    });
    const isFormDirty = form.formState.isDirty;
    const [pricing, price, minPrice, discount, oneTimeInStagesBilling, description, terms, serviceName] = form.watch([
        'pricing',
        'price',
        'minPrice',
        'discount',
        'oneTimeInStagesBilling',
        'description',
        'terms',
        'serviceName'
    ]);
    const hasDiscountError = Boolean(form.formState.errors.discount);
    const isDescriptionDirty = !!form.formState.dirtyFields.description && quillToText(description) !== quillToText(defaultValues.description);
    const isTermsDirty = !!form.formState.dirtyFields.terms && quillToText(terms) !== quillToText(defaultValues.terms);
    const isContentDirty = isDescriptionDirty || isTermsDirty;
    useEffect(()=>{
        if (oneTimeInStagesBilling && form.getValues().discount.type === DiscountType.Value) form.setValue('discount', {
            type: DiscountType.Value,
            amount: ''
        });
    }, [
        oneTimeInStagesBilling
    ]);
    useEffect(()=>{
        const serviceQboMapping = getDefaultServiceMapping(service === null || service === void 0 ? void 0 : service.integrations.qboIntegratedId(), (qboItems === null || qboItems === void 0 ? void 0 : qboItems.items) || []);
        const serviceXeroMapping = getDefaultServiceMapping(service === null || service === void 0 ? void 0 : service.integrations.xeroIntegratedId(), (xeroItems === null || xeroItems === void 0 ? void 0 : xeroItems.items) || []);
        form.setValue('serviceQboMapping', serviceQboMapping);
        form.setValue('serviceXeroMapping', serviceXeroMapping);
    }, [
        qboItems === null || qboItems === void 0 ? void 0 : qboItems.items,
        service === null || service === void 0 ? void 0 : service.integrations,
        xeroItems === null || xeroItems === void 0 ? void 0 : xeroItems.items
    ]);
    useEffect(()=>{
        if (showQboIntegration) refreshQboItems();
        if (showXeroIntegration) refreshXeroItems();
        if (showFinancialCentsIntegration) handlePromise(fetchFinancialCentsItems());
        if (showKarbonIntegration) handlePromise(fetchKarbonItems());
        if (showClientHubIntegration) handlePromise(fetchClientHubItems());
        if (showKeeperIntegration) handlePromise(fetchKeeperItems());
    }, [
        showQboIntegration,
        showFinancialCentsIntegration,
        showKarbonIntegration,
        showClientHubIntegration,
        showKeeperIntegration,
        showXeroIntegration
    ]);
    useEffect(()=>{
        if (!rightSectionRef.current) return;
        const updateDrawerWidth = ()=>{
            var _rightSectionRef_current;
            const width = (_rightSectionRef_current = rightSectionRef.current) === null || _rightSectionRef_current === void 0 ? void 0 : _rightSectionRef_current.getBoundingClientRect().width;
            if (width) {
                // 24px padding, 36px header gap, 36px footer gap, 12px margin of the drawer twice
                setDrawerWidth(width + 24 + 36 + 36 - 12 - 12);
            }
        };
        updateDrawerWidth();
        const resizeObserver = new ResizeObserver(updateDrawerWidth);
        resizeObserver.observe(rightSectionRef.current);
        return ()=>{
            resizeObserver.disconnect();
        };
    }, []);
    const discountedPrice = useMemo(()=>getPriceAfterDiscount({
            pricing,
            price,
            minPrice,
            discount,
            texts: sbTexts
        }), [
        pricing,
        price,
        minPrice,
        discount
    ]);
    const updateExistingServicesForm = useForm({
        mode: 'all',
        defaultValues: {
            [ServiceTemplateUsedIn.Draft]: false,
            [ServiceTemplateUsedIn.ProposalTemplate]: false
        }
    });
    const onSubmit = async (value)=>{
        if (!isFormDirty && !isContentDirty) {
            // Do nothing if form hasn't changed.
            return onClose();
        }
        let serviceContentObj = null;
        if ((serviceUsedIn === null || serviceUsedIn === void 0 ? void 0 : serviceUsedIn.some((usedIn)=>[
                ServiceTemplateUsedIn.ProposalTemplate,
                ServiceTemplateUsedIn.Draft
            ].includes(usedIn))) && isContentDirty) {
            const confirmed = await overlayContext.showModal({
                biName: 'service_builder_update_existing_confirm',
                closeOnClickOutside: true,
                content: (param)=>{
                    let { dismissModal } = param;
                    return /*#__PURE__*/ _jsx(FormProvider, _object_spread_props(_object_spread({}, updateExistingServicesForm), {
                        children: /*#__PURE__*/ _jsx(ConfirmationModal, {
                            bi: {
                                primaryButton: {
                                    name: 'service_builder_confirm'
                                },
                                secondaryButton: {
                                    name: 'service_builder_cancel'
                                }
                            },
                            width: "660px",
                            testId: "update-existing-services-modal",
                            title: sbTexts.updateExisting.title(),
                            textAlignCenter: false,
                            content: /*#__PURE__*/ _jsx(UpdateExistingServicesContent, {
                                serviceUsedIn: serviceUsedIn
                            }),
                            primaryButtonLabel: sbTexts.updateExisting.saveButton(),
                            primaryButtonTestId: "update-existing-services-modal-primary-btn",
                            onPrimaryButtonClick: ()=>dismissModal(true),
                            secondaryButtonLabel: sbTexts.updateExisting.cancelButton(),
                            onSecondaryButtonClick: ()=>{
                                dismissModal(false);
                            }
                        })
                    }));
                }
            });
            if (!confirmed) {
                updateExistingServicesForm.reset();
                return;
            }
            const { draft, proposalTemplate } = updateExistingServicesForm.getValues();
            const updateExistingServices = {
                [ServiceTemplateUsedIn.Draft]: draft,
                [ServiceTemplateUsedIn.ProposalTemplate]: proposalTemplate
            };
            serviceContentObj = {
                description: value.description,
                terms: value.terms,
                updateRelationshipDrafts: updateExistingServices.draft,
                updateProposalTemplates: updateExistingServices.proposalTemplate
            };
        }
        const outputService = outputToService({
            output: value,
            agreementData,
            originalService: service,
            applyOutputToChanges,
            isAgreement: serviceOrigin === 'agreement',
            generateUniqueId
        });
        if (onSave) {
            setSaving(true);
            const result = await onSave(outputService, serviceContentObj || undefined);
            setSaving(false);
            if (!result.success) return;
        }
        dismissModal(outputService, {
            force: true
        });
    };
    registerCanDismiss(async ()=>{
        if (!isFormDirty) return true;
        const confirmTitle = service ? sbTexts.modal.discardChanges() : sbTexts.modal.cancelService();
        return openUnsavedChangesModal({
            testId: 'sb-confirm-cancel',
            title: confirmTitle,
            primaryButtonLabel: confirmTitle,
            content: sbTexts.modal.unsavedChangesContent()
        });
    });
    const buttonSize = isMobileDevice ? 'xsmall' : 'medium';
    const actionButtons = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Button, {
                bi: {
                    name: 'cancel'
                },
                skin: "primaryOutline",
                testId: "sb-cancel",
                onClick: onClose,
                size: buttonSize,
                children: sbTexts.buttons.cancel()
            }),
            /*#__PURE__*/ _jsx(Button, {
                bi: {
                    name: 'save'
                },
                type: "submit",
                testId: "sb-submit",
                form: formId,
                loading: saving,
                size: buttonSize,
                children: sbTexts.buttons.save()
            })
        ]
    });
    const headerMainAction = {
        text: sbTexts.buttons.save(),
        icon: /*#__PURE__*/ _jsx(Checkmark, {}),
        onClick: ()=>undefined,
        type: 'submit',
        form: formId,
        loading: saving,
        testId: 'sb-submit',
        bi: {
            name: 'save'
        }
    };
    const headerSecondaryAction = {
        text: sbTexts.buttons.cancel(),
        icon: /*#__PURE__*/ _jsx(CloseCross, {}),
        onClick: onClose,
        testId: 'sb-cancel',
        bi: {
            name: 'cancel'
        }
    };
    const showAiDescriptionPanel = !isBundle && !isMobileDevice;
    const disableAiDescriptionPanel = showAiDescriptionPanel && form.watch('serviceName') === '';
    const onHelpMeWriteClick = showAiDescriptionPanel ? ()=>{
        logBI(17001);
        setIsHelpMeWriteDrawerOpen((prev)=>!prev);
    } : undefined;
    const content = /*#__PURE__*/ _jsx(FormProvider, _object_spread_props(_object_spread({}, form), {
        children: /*#__PURE__*/ _jsxs("form", {
            id: formId,
            className: cn(styles.content, {
                [styles.mobile]: isMobileDevice
            }),
            "data-testid": "service-builder-form",
            "data-mobile": isMobileDevice,
            noValidate: true,
            onSubmit: form.handleSubmit(onSubmit),
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: styles.leftSection,
                    children: /*#__PURE__*/ _jsxs(MobileGroupingCard, {
                        testId: "sb-mobile-card-name",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: styles.rowGap,
                                children: /*#__PURE__*/ _jsx(ServiceNameTextField, {
                                    testId: "sb-name",
                                    disabled: isHelpMeWriteDrawerOpen || isNameFieldDisabled(service, serviceOrigin),
                                    serviceNameBlacklist: serviceNameBlacklist
                                })
                            }),
                            /*#__PURE__*/ _jsx(ServiceDescription, {
                                testId: "sb-description",
                                onHelpMeWriteClick: onHelpMeWriteClick,
                                disabledHelpMeWrite: disableAiDescriptionPanel,
                                isDrawerOpen: isHelpMeWriteDrawerOpen,
                                disabled: isHelpMeWriteDrawerOpen
                            }),
                            SERVICE_LEVEL_TERMS && !isBundle && /*#__PURE__*/ _jsx(ServiceTerms, {
                                testId: "sb-terms",
                                disabled: isHelpMeWriteDrawerOpen
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: styles.sep
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: styles.rightSection,
                    ref: rightSectionRef,
                    children: [
                        /*#__PURE__*/ _jsxs(MobileGroupingCard, {
                            testId: "sb-mobile-card-billing",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: cn(styles.topRightSection, {
                                        [styles.hasDiscountedPrice]: discountedPrice,
                                        [styles.isBundle]: isBundle
                                    }),
                                    children: [
                                        /*#__PURE__*/ _jsx(OccurrenceTextField, {
                                            serviceOrigin: serviceOrigin,
                                            service: service
                                        }),
                                        /*#__PURE__*/ _jsx(PricingTextField, {
                                            isBundle: isBundle,
                                            serviceOrigin: serviceOrigin,
                                            isExistingService: !!service
                                        }),
                                        pricing === 'unit' && /*#__PURE__*/ _jsx(UnitNameTextField, {
                                            testId: "sb-unit-name"
                                        }),
                                        pricing === 'range' && /*#__PURE__*/ _jsx(MinPriceTextField, {
                                            testId: "sb-min-price"
                                        }),
                                        !isBundle && /*#__PURE__*/ _jsx(PriceTextField, {
                                            testId: "sb-price"
                                        }),
                                        !isBundle && /*#__PURE__*/ _jsx(DiscountInput, {
                                            testId: "sb-discount"
                                        })
                                    ]
                                }),
                                discountedPrice && !isBundle && !hasDiscountError && /*#__PURE__*/ _jsx(Typography, {
                                    className: styles.rowGap,
                                    testId: "sb-discounted-price",
                                    children: sbTexts.priceAfterDiscount({
                                        price: discountedPrice
                                    })
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: styles.rowGap,
                                    children: /*#__PURE__*/ _jsx(Controller, {
                                        name: "oneTimeInStagesBilling",
                                        control: form.control,
                                        rules: {
                                            validate: ()=>{
                                                if (!oneTimeInStagesBilling) return true;
                                                const stages = oneTimeInStagesBilling.stages();
                                                const valueType = oneTimeInStagesBilling.valueType();
                                                const totalRate = new Decimal(price || 0);
                                                const hasValueLevelError = stages.some((stage, index)=>{
                                                    const isLast = index === stages.length - 1;
                                                    const totalSumExceedsRate = stages.reduce((acc, stage)=>acc.plus(stage.value), new Decimal(0)).gt(isValueTypeDollars(valueType) ? totalRate : new Decimal(100));
                                                    return !!getHelperText({
                                                        stage,
                                                        isLast,
                                                        valueType,
                                                        totalRate,
                                                        totalSumExceedsRate,
                                                        texts: stagesTexts
                                                    });
                                                });
                                                if (hasValueLevelError) return false;
                                                if (!AUTOMATIC_STAGES) return true;
                                                const effectiveDateDate = (agreementData === null || agreementData === void 0 ? void 0 : agreementData.effectiveDate) ? fromDateStampObject(agreementData.effectiveDate) : null;
                                                const effectiveDateString = effectiveDateDate ? formatDate(effectiveDateDate, 'yyyy-MM-dd') : null;
                                                const now = new Date();
                                                const scheduleContext = resolveStageScheduleContext({
                                                    isTemplate: serviceOrigin === 'template',
                                                    isAmendment: !!(agreementData === null || agreementData === void 0 ? void 0 : agreementData.isActiveAgreement()) || !!service && isFromAmendment(service),
                                                    effectiveDate: effectiveDateDate,
                                                    now
                                                });
                                                return !oneTimeInStagesBilling.hasInvalidStages({
                                                    scheduleContext: {
                                                        context: scheduleContext,
                                                        effectiveDate: effectiveDateString,
                                                        today: formatDate(now, 'yyyy-MM-dd')
                                                    }
                                                });
                                            }
                                        },
                                        render: ()=>/*#__PURE__*/ _jsx(BillingTriggerDropdown, {
                                                service: service,
                                                effectiveDate: (agreementData === null || agreementData === void 0 ? void 0 : agreementData.effectiveDate) || null,
                                                serviceOrigin: serviceOrigin,
                                                isAmendmentContext: !!(agreementData === null || agreementData === void 0 ? void 0 : agreementData.isActiveAgreement()) || !!service && isFromAmendment(service)
                                            })
                                    })
                                }),
                                [
                                    'hour',
                                    'unit'
                                ].includes(pricing) && /*#__PURE__*/ _jsx(PreapprovedSection, {})
                            ]
                        }),
                        isBundle && /*#__PURE__*/ _jsx(MobileGroupingCard, {
                            testId: "sb-mobile-card-included-services",
                            children: /*#__PURE__*/ _jsx("div", {
                                className: styles.rowGap,
                                children: /*#__PURE__*/ _jsx(Controller, {
                                    name: "bundle",
                                    control: form.control,
                                    render: (param)=>{
                                        let { field: { value, onChange } } = param;
                                        return /*#__PURE__*/ _jsx(IncludedServices, {
                                            tooltip: sbTexts.bundlePriceTooltip(),
                                            mode: "delete",
                                            value: value || [],
                                            onChange: (value)=>{
                                                onChange(value);
                                                const price = value.reduce((acc, service)=>acc.plus(service.rate), new Decimal(0));
                                                form.setValue('price', price.toString());
                                            }
                                        });
                                    }
                                })
                            })
                        }),
                        showQboIntegration && /*#__PURE__*/ _jsx(MobileGroupingCard, {
                            testId: "sb-mobile-card-qbo",
                            children: /*#__PURE__*/ _jsx("div", {
                                className: styles.rowGap,
                                children: /*#__PURE__*/ _jsx(ServiceAccountingField, {
                                    controlName: "serviceQboMapping",
                                    accounting: Accounting.QBO,
                                    accountingItems: isBundle ? (qboItems === null || qboItems === void 0 ? void 0 : qboItems.items) || [] : filterServiceOnlyTemplates((qboItems === null || qboItems === void 0 ? void 0 : qboItems.items) || []),
                                    isDisabled: disableIntegrationsMapping,
                                    testId: "qbo-mapping"
                                })
                            })
                        }),
                        showXeroIntegration && /*#__PURE__*/ _jsx(MobileGroupingCard, {
                            testId: "sb-mobile-card-xero",
                            children: /*#__PURE__*/ _jsx("div", {
                                className: styles.rowGap,
                                children: /*#__PURE__*/ _jsx(ServiceAccountingField, {
                                    controlName: "serviceXeroMapping",
                                    accounting: Accounting.Xero,
                                    accountingItems: (xeroItems === null || xeroItems === void 0 ? void 0 : xeroItems.items) || null,
                                    isDisabled: disableIntegrationsMapping,
                                    testId: "xero-mapping"
                                })
                            })
                        }),
                        showPracticeManagementIntegration(showFinancialCentsIntegration) && /*#__PURE__*/ _jsx(MobileGroupingCard, {
                            testId: "sb-mobile-card-financial-cents",
                            children: /*#__PURE__*/ _jsx("div", {
                                className: styles.rowGap,
                                children: /*#__PURE__*/ _jsx(Controller, {
                                    name: "financialCentsMapping",
                                    control: form.control,
                                    render: (param)=>{
                                        let { field: { value, onChange } } = param;
                                        return /*#__PURE__*/ _jsx(PMMapServiceField, {
                                            testId: "financial-cents-mapping",
                                            label: sbTexts.pmMapping.label({
                                                pmName: getPMDetails(PracticeManagement.FinancialCents).name
                                            }),
                                            options: financialCentsItems,
                                            loading: !financialCentsItems,
                                            disabled: disableIntegrationsMapping,
                                            tooltip: disableIntegrationsMapping ? sbTexts.pmMapping.disabledTooltip({
                                                pmName: getPMDetails(PracticeManagement.FinancialCents).name,
                                                pmItems: getPMDetails(PracticeManagement.FinancialCents).items
                                            }) : '',
                                            value: value,
                                            onChange: onChange
                                        });
                                    }
                                })
                            })
                        }),
                        showPracticeManagementIntegration(showKarbonIntegration) && /*#__PURE__*/ _jsx(MobileGroupingCard, {
                            testId: "sb-mobile-card-karbon",
                            children: /*#__PURE__*/ _jsx("div", {
                                className: styles.rowGap,
                                children: /*#__PURE__*/ _jsx(Controller, {
                                    name: "karbonMapping",
                                    control: form.control,
                                    render: (param)=>{
                                        let { field: { value, onChange } } = param;
                                        return /*#__PURE__*/ _jsx(PMMapServiceField, {
                                            testId: "karbon-mapping",
                                            label: sbTexts.pmMapping.label({
                                                pmName: getPMDetails(PracticeManagement.Karbon).name
                                            }),
                                            options: karbonItems,
                                            loading: !karbonItems,
                                            disabled: disableIntegrationsMapping,
                                            tooltip: disableIntegrationsMapping ? sbTexts.pmMapping.disabledTooltip({
                                                pmName: getPMDetails(PracticeManagement.Karbon).name,
                                                pmItems: getPMDetails(PracticeManagement.Karbon).items
                                            }) : '',
                                            value: value,
                                            onChange: onChange
                                        });
                                    }
                                })
                            })
                        }),
                        showPracticeManagementIntegration(showClientHubIntegration) && /*#__PURE__*/ _jsx(MobileGroupingCard, {
                            testId: "sb-mobile-card-client-hub",
                            children: /*#__PURE__*/ _jsx("div", {
                                className: styles.rowGap,
                                children: /*#__PURE__*/ _jsx(Controller, {
                                    name: "clientHubMapping",
                                    control: form.control,
                                    render: (param)=>{
                                        let { field: { value, onChange } } = param;
                                        return /*#__PURE__*/ _jsx(PMMapServiceField, {
                                            testId: "client-hub-mapping",
                                            label: sbTexts.pmMapping.label({
                                                pmName: getPMDetails(PracticeManagement.ClientHub).name
                                            }),
                                            options: clientHubItems,
                                            loading: !clientHubItems,
                                            disabled: disableIntegrationsMapping,
                                            tooltip: disableIntegrationsMapping ? sbTexts.pmMapping.disabledTooltip({
                                                pmName: getPMDetails(PracticeManagement.ClientHub).name,
                                                pmItems: getPMDetails(PracticeManagement.ClientHub).items
                                            }) : '',
                                            value: value,
                                            onChange: onChange
                                        });
                                    }
                                })
                            })
                        }),
                        showPracticeManagementIntegration(showKeeperIntegration) && /*#__PURE__*/ _jsx(MobileGroupingCard, {
                            testId: "sb-mobile-card-keeper",
                            children: /*#__PURE__*/ _jsx("div", {
                                className: styles.rowGap,
                                children: /*#__PURE__*/ _jsx(Controller, {
                                    name: "keeperMapping",
                                    control: form.control,
                                    render: (param)=>{
                                        let { field: { value, onChange } } = param;
                                        return /*#__PURE__*/ _jsx(PMMapServiceField, {
                                            testId: "keeper-mapping",
                                            label: sbTexts.pmMapping.label({
                                                pmName: getPMDetails(PracticeManagement.Keeper).name
                                            }),
                                            options: keeperItems,
                                            loading: !keeperItems,
                                            disabled: disableIntegrationsMapping,
                                            tooltip: disableIntegrationsMapping ? sbTexts.pmMapping.disabledTooltip({
                                                pmName: getPMDetails(PracticeManagement.Keeper).name,
                                                pmItems: getPMDetails(PracticeManagement.Keeper).items
                                            }) : '',
                                            value: value,
                                            onChange: onChange
                                        });
                                    }
                                })
                            })
                        })
                    ]
                })
            ]
        })
    }));
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            isFullScreen ? /*#__PURE__*/ _jsxs(FullPageModal, {
                testId: rootTestId,
                "data-fullpage": fullPageModal,
                contentSkin: "light",
                onClose: onClose,
                children: [
                    /*#__PURE__*/ _jsx(FullPageModal.HeaderStart, {
                        children: headerText
                    }),
                    content,
                    /*#__PURE__*/ _jsx(FullPageModalHeaderActions, {
                        mainAction: headerMainAction,
                        secondaryAction: headerSecondaryAction
                    })
                ]
            }) : /*#__PURE__*/ _jsx(CustomModal, {
                bi: {
                    closeButton: {
                        name: 'service_builder_modal_close'
                    }
                },
                width: 1200,
                maxWidth: "90%",
                testId: rootTestId,
                "data-fullpage": fullPageModal,
                header: /*#__PURE__*/ _jsx(Typography, {
                    variant: "h6",
                    weight: "bold",
                    children: headerText
                }),
                headerClassName: styles.modalHeader,
                footer: /*#__PURE__*/ _jsxs("div", {
                    className: styles.modalFooter,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            children: sbTexts.agreementOnlyNote({
                                entityType: isBundle ? 'bundle' : 'service'
                            })
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: styles.modalFooterEnd,
                            children: actionButtons
                        })
                    ]
                }),
                onClose: onClose,
                children: content
            }),
            showAiDescriptionPanel && /*#__PURE__*/ _jsx(ServiceDescriptionHelpMeWriteDrawer, {
                open: isHelpMeWriteDrawerOpen,
                onClose: ()=>setIsHelpMeWriteDrawerOpen(false),
                drawerWidth: drawerWidth,
                serviceName: serviceName,
                serviceDescription: description,
                serviceId: (_ref = service === null || service === void 0 ? void 0 : service.id) !== null && _ref !== void 0 ? _ref : 'NA',
                onInsert: (parsedRichText)=>{
                    form.setValue('description', normalizeDescription(parsedRichText), {
                        shouldDirty: true
                    });
                }
            })
        ]
    });
}
export default function ServiceBuilder(props) {
    return /*#__PURE__*/ _jsx(MobileDeviceEnhancer, {
        children: /*#__PURE__*/ _jsx(ServiceBuilderContent, _object_spread({}, props))
    });
}
